import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  formatMoney,
  INSURER_WRITE_OFF_REASON_WORDS,
  INSURER_WRITE_OFF_REASONS,
  moneyFromCents,
  type InsurerWriteOffReason,
} from '@wodi/domain';
import type { AmountView, InsurerClaimDetail } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  Section,
  Select,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  askWriteOff,
  decideWriteOff,
  insuranceKeys,
  insurerClaimQuery,
  issueInvoice,
  recordRejection,
  resubmit,
} from '../api/insurance.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PREAUTH_STATE_WORDS } from '../insurance/bill-insurance.tsx';
import { coverLine } from '../insurance/cover.tsx';
import styles from '../insurance/insurance.module.css';
import { ClaimFormSheet } from '../insurance/prints.tsx';
import { TableScroll } from '../layout/screen.tsx';
import { formatDateOnly, formatInstant, formatIsoDate } from '../patients/format.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// One insurer claim (docs/plans/phase2-private-insurance.md §6 item 5): problems first,
// then cover, lines, approvals and history; actions by state and permission; the printable
// claim form.

const route = getRouteApi('/_app/insurance/claims/$claimId');

const kes = (a: AmountView) => formatMoney(moneyFromCents(BigInt(a.cents)));
const kesColumn = (a: AmountView) =>
  formatMoney(moneyFromCents(BigInt(a.cents)), { showCurrency: false });

function useRefresh(claimId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: insuranceKeys.claim(claimId) });
    await queryClient.invalidateQueries({ queryKey: insuranceKeys.all });
  };
}

function RejectionForm({ claim, onDone }: { claim: InsurerClaimDetail; onDone: () => void }) {
  const refresh = useRefresh(claim.id);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      recordRejection(claim.id, { expectedVersion: claim.version, reason: reason.trim() }),
    onSuccess: async () => {
      toasts.show({ title: 'Rejection recorded' });
      await refresh();
      onDone();
    },
  });
  return (
    <form
      className={styles.form}
      aria-label="Record the insurer’s rejection"
      onSubmit={(e) => {
        e.preventDefault();
        setTried(true);
        if (reason.trim().length >= 2) save.mutate();
      }}
    >
      <TextField
        label="Insurer’s reason"
        description="As the insurer gave it."
        value={reason}
        onChange={setReason}
        {...(tried && reason.trim().length < 2
          ? { errorMessage: 'Enter the insurer’s reason.' }
          : {})}
      />
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, "The rejection couldn't be recorded.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
          Record rejection
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function WriteOffForm({ claim, onDone }: { claim: InsurerClaimDetail; onDone: () => void }) {
  const refresh = useRefresh(claim.id);
  const outstanding = BigInt(claim.outstanding.cents);
  const [amount, setAmount] = useState<bigint | null>(outstanding);
  const [reason, setReason] = useState<InsurerWriteOffReason>('short_payment');
  const [note, setNote] = useState('');
  const bad = amount === null || amount <= 0n || amount > outstanding;
  const ask = useMutation({
    mutationFn: () =>
      askWriteOff(claim.id, {
        amount: { currency: 'KES', cents: (amount ?? 0n).toString() },
        reasonCode: reason,
        ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Write-off asked for' });
      await refresh();
      onDone();
    },
  });
  return (
    <form
      className={styles.form}
      aria-label="Ask to write off"
      onSubmit={(e) => {
        e.preventDefault();
        if (!bad) ask.mutate();
      }}
    >
      <div className={styles.fields}>
        <MoneyField
          label="Amount"
          description={`At most ${kes(claim.outstanding)}, what the insurer still owes.`}
          value={amount}
          onChange={(cents) => {
            setAmount(cents);
          }}
          {...(amount !== null && amount > outstanding
            ? { errorMessage: 'That is more than the insurer still owes.' }
            : {})}
        />
        <Select
          label="Reason"
          options={INSURER_WRITE_OFF_REASONS.map((r) => ({
            id: r,
            label: INSURER_WRITE_OFF_REASON_WORDS[r],
          }))}
          value={reason}
          onChange={(v) => {
            setReason((v ?? 'other') as InsurerWriteOffReason);
          }}
        />
        <TextField label="Note" description="Required for Other." value={note} onChange={setNote} />
      </div>
      {ask.error === null ? null : (
        <Notice tone="error">{errorText(ask.error, "The write-off couldn't be asked for.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={ask.isPending} pendingLabel="Asking…">
          Ask to write off
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Actions({ claim }: { claim: InsurerClaimDetail }) {
  const online = useOnline();
  const refresh = useRefresh(claim.id);
  const { data: session } = useSuspenseQuery(sessionQuery);
  const [open, setOpen] = useState<'reject' | 'write_off' | null>(null);
  const manage = session !== null && can(session, 'insurance.claims.manage');
  const mayWriteOff = session !== null && can(session, 'insurance.write_off.request');
  const submit = useMutation({
    mutationFn: () =>
      issueInvoice({
        payerId: claim.payer.id,
        claims: [{ id: claim.id, expectedVersion: claim.version }],
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Claim sent on an invoice' });
      await refresh();
    },
  });
  const again = useMutation({
    mutationFn: () => resubmit(claim.id, claim.version),
    onSuccess: async () => {
      toasts.show({ title: 'Claim sent again' });
      await refresh();
    },
  });
  const openWriteOff = claim.writeOffs.some((w) => w.status === 'requested');
  const error = submit.error ?? again.error;
  return (
    <Section title="Actions">
      <div className={styles.actions}>
        {manage && claim.state === 'draft' ? (
          <Button
            variant="primary"
            isDisabled={!online || !claim.canSubmit}
            isPending={submit.isPending}
            pendingLabel="Sending…"
            onPress={() => {
              submit.mutate();
            }}
          >
            Submit claim
          </Button>
        ) : null}
        {manage && claim.state === 'submitted' ? (
          <Button
            isDisabled={!online}
            onPress={() => {
              setOpen('reject');
            }}
          >
            Record rejection
          </Button>
        ) : null}
        {manage && claim.state === 'rejected' ? (
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={again.isPending}
            pendingLabel="Sending…"
            onPress={() => {
              again.mutate();
            }}
          >
            Submit again
          </Button>
        ) : null}
        {mayWriteOff &&
        !openWriteOff &&
        ['submitted', 'partly_paid', 'rejected'].includes(claim.state) ? (
          <Button
            isDisabled={!online}
            onPress={() => {
              setOpen('write_off');
            }}
          >
            Ask to write off
          </Button>
        ) : null}
      </div>
      {claim.state === 'draft' && !claim.canSubmit ? (
        <p className={styles.detail}>Fix the problems above before submitting.</p>
      ) : null}
      {error === null ? null : (
        <Notice tone="error">{errorText(error, 'That didn’t work. Try again.')}</Notice>
      )}
      {open === 'reject' ? (
        <RejectionForm
          claim={claim}
          onDone={() => {
            setOpen(null);
          }}
        />
      ) : null}
      {open === 'write_off' ? (
        <WriteOffForm
          claim={claim}
          onDone={() => {
            setOpen(null);
          }}
        />
      ) : null}
    </Section>
  );
}

function WriteOffs({ claim }: { claim: InsurerClaimDetail }) {
  const online = useOnline();
  const refresh = useRefresh(claim.id);
  const { data: session } = useSuspenseQuery(sessionQuery);
  const [note, setNote] = useState('');
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      decideWriteOff(id, action, note.trim() === '' ? undefined : note.trim()),
    onSuccess: async (_r, v) => {
      toasts.show({ title: v.action === 'approve' ? 'Write-off approved' : 'Write-off rejected' });
      setNote('');
      await refresh();
    },
  });
  if (claim.writeOffs.length === 0) return null;
  const mayDecide = session !== null && can(session, 'insurance.write_off.approve');
  return (
    <Section title="Write-offs">
      <ul className={styles.list} aria-label="Write-offs">
        {claim.writeOffs.map((w) => (
          <li key={w.id} className={styles.row}>
            <span className={styles.grow}>
              {`${kes(w.amount)}: ${INSURER_WRITE_OFF_REASON_WORDS[w.reasonCode]}${w.reasonNote === null ? '' : ` (${w.reasonNote})`}. Asked by ${w.requestedBy.displayName}, ${formatInstant(w.requestedAt)}.`}
              {w.decidedBy === null
                ? ''
                : ` ${w.status === 'approved' ? 'Approved' : 'Rejected'} by ${w.decidedBy.displayName}${w.decisionNote === null ? '' : `: ${w.decisionNote}`}.`}
            </span>
            {w.status === 'requested' && mayDecide ? (
              <div className={styles.actions}>
                <TextField
                  label="Decision note"
                  description="Required to reject."
                  value={note}
                  onChange={setNote}
                />
                <Button
                  variant="primary"
                  isDisabled={!online}
                  isPending={decide.isPending}
                  pendingLabel="Saving…"
                  onPress={() => {
                    decide.mutate({ id: w.id, action: 'approve' });
                  }}
                >
                  Approve write-off
                </Button>
                <Button
                  isDisabled={!online}
                  onPress={() => {
                    decide.mutate({ id: w.id, action: 'reject' });
                  }}
                >
                  Reject write-off
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {decide.error === null ? null : (
        <Notice tone="error">{errorText(decide.error, "The decision couldn't be saved.")}</Notice>
      )}
    </Section>
  );
}

export function InsuranceClaimPage() {
  const { claimId } = route.useParams();
  const claim = useQuery(insurerClaimQuery(claimId));
  const c = claim.data;
  return (
    <div className={styles.page}>
      {c === undefined ? null : <PatientBannerLoader patientId={c.patient.id} />}
      <PageHeader
        title="Insurer claim"
        actions={
          <Link to="/insurance" search={{ view: 'all' }}>
            All insurer claims
          </Link>
        }
      />
      {claim.isPending ? <LoadingBlock label="Loading the claim" rows={5} /> : null}
      {claim.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void claim.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(claim.error, "The claim couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {c === undefined ? null : (
        <>
          <Section title="Claim">
            <dl className={styles.facts}>
              <dt>Claim number</dt>
              <dd className={styles.id}>{c.claimNumber}</dd>
              <dt>State</dt>
              <dd className={styles.sentence}>{c.sentence}</dd>
              <dt>Insurer</dt>
              <dd>{coverLine(c.cover)}</dd>
              <dt>Member</dt>
              <dd>
                <span className={styles.id}>{c.cover.memberNumber}</span>
                {c.cover.relationship === 'dependant' ? (
                  <>
                    {', dependant of '}
                    {c.cover.principalName ?? 'the principal'}{' '}
                    <span className={styles.id}>{c.cover.principalMemberNumber}</span>
                  </>
                ) : (
                  ', principal member'
                )}
              </dd>
              <dt>Visit</dt>
              <dd>
                <span className={styles.id}>{c.visit.visitNumber}</span>
                {`, ${formatDateOnly(c.visit.startedAt)}`}
              </dd>
              {c.invoiceNumber === null ? null : (
                <>
                  <dt>Invoice</dt>
                  <dd className={styles.id}>{c.invoiceNumber}</dd>
                </>
              )}
              <dt>{c.state === 'draft' ? 'Would claim' : 'Claimed'}</dt>
              <dd>{kes(c.state === 'draft' ? (c.estimated ?? c.claimed) : c.claimed)}</dd>
              {c.state === 'draft' ? null : (
                <>
                  <dt>Paid</dt>
                  <dd>{kes(c.paid)}</dd>
                  <dt>Written off</dt>
                  <dd>{kes(c.writtenOff)}</dd>
                  <dt>Outstanding</dt>
                  <dd>{kes(c.outstanding)}</dd>
                </>
              )}
            </dl>
          </Section>
          {c.problems.length > 0 ? (
            <Section title="To fix before sending">
              <ul className={styles.list}>
                {c.problems.map((p) => (
                  <li key={`${p.code}-${p.subjectId ?? ''}`} className={styles.row}>
                    {p.sentence}
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          <Actions claim={c} />
          <Section title="Lines">
            {c.lines.length === 0 ? (
              <p className={styles.detail}>Nothing on this visit is for the insurer to pay yet.</p>
            ) : (
              <TableScroll>
                <table className={styles.table} aria-label="Claim lines">
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Service</th>
                      <th scope="col" className={styles.number}>
                        Qty
                      </th>
                      <th scope="col" className={styles.number}>
                        Total (KES)
                      </th>
                      <th scope="col" className={styles.number}>
                        Patient (KES)
                      </th>
                      <th scope="col" className={styles.number}>
                        Insurer (KES)
                      </th>
                      <th scope="col">Approval</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.lines.map((l) => (
                      <tr key={l.chargeId}>
                        <td>{formatIsoDate(l.serviceDate)}</td>
                        <td>
                          {l.itemName} <span className={styles.id}>{l.itemCode}</span>
                        </td>
                        <td className={styles.number}>{l.quantity}</td>
                        <td className={styles.number}>{kesColumn(l.total)}</td>
                        <td className={styles.number}>{kesColumn(l.patient)}</td>
                        <td className={styles.number}>{kesColumn(l.payer)}</td>
                        <td className={styles.id}>{l.preauthReference ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Section>
          {c.preauths.length > 0 ? (
            <Section title="Insurer approvals">
              <ul className={styles.list}>
                {c.preauths.map((p) => (
                  <li key={p.id} className={styles.row}>
                    {`${p.serviceName ?? 'The whole visit'}: ${PREAUTH_STATE_WORDS[p.state]}`}
                    {p.insurerReference === null ? null : (
                      <span className={styles.id}>{p.insurerReference}</span>
                    )}
                    {p.approved === null ? null : <span>{`${kes(p.approved)} approved`}</span>}
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          <WriteOffs claim={c} />
          <Section title="History">
            <ul className={styles.list} aria-label="Claim history">
              {c.events.map((e) => (
                <li key={`${e.kind}-${e.occurredAt}`} className={styles.row}>
                  <span className={styles.grow}>
                    {e.sentence}
                    {e.amount === null ? '' : `, ${kes(e.amount)}`}
                    {e.note === null ? '' : `: ${e.note}`}
                  </span>
                  <span
                    className={styles.detail}
                  >{`${formatInstant(e.occurredAt)}, ${e.actor.displayName}`}</span>
                </li>
              ))}
            </ul>
          </Section>
          <ClaimFormSheet claim={c} />
        </>
      )}
    </div>
  );
}
